import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Droplets, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative border-r border-white/10">
        <img src="https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwY2FyJTIwbW9kZXJufGVufDB8fHx8MTc4NDI1MTIxMnww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <div className="overline text-volt mb-2">Earlwood Automatic Car Wash</div>
          <h2 className="font-head font-black text-5xl uppercase tracking-tight leading-none">Welcome<br />Back.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 bg-volt flex items-center justify-center"><Droplets className="w-5 h-5 text-white" /></div>
            <span className="font-head font-extrabold text-xl tracking-tight uppercase">Earlwood</span>
          </Link>
          <div className="overline text-volt mb-2">Member Login</div>
          <h1 className="font-head font-extrabold text-3xl uppercase tracking-tight mb-8">Sign In</h1>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="overline text-muted-foreground block mb-2">Email</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Password</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-3">{error}</div>}
            <button data-testid="login-submit-btn" disabled={loading} type="submit"
              className="w-full bg-volt text-white py-3 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            No account? <Link to="/register" data-testid="login-to-register" className="text-volt hover:underline">Join now</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
